import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { canUseMultiEstablishmentContext, useAuthStore } from '@/stores/auth';
import { useAuthorizationStore } from '@/stores/authorization';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft } from 'lucide-react';

const MODULES_REQUIRING_CONTEXT = ['establishment', 'medical_company'];

export const EstablishmentContextModal = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    isAuthenticated,
    user,
    contextSelectionRequired,
    contextSelectionRedirectRoute,
    isSelectingContext,
    selectEstablishmentContext,
  } = useAuthStore();

  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const options = user?.establishments ?? [];
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const moduleRequiresContext =
    selectedModuleKey !== null &&
    MODULES_REQUIRING_CONTEXT.includes(selectedModuleKey);
  const allowsMultipleContexts = selectedModuleKey === 'medical_company';
  const canUseContext = canUseMultiEstablishmentContext(user);
  const hasSelectedContext = Boolean(user?.establishment_config_id);
  const shouldRequestContext =
    canUseContext && (contextSelectionRequired || !hasSelectedContext);

  const allOptionIDs = useMemo(
    () => options.map((item) => item.establishment_config_id),
    [options],
  );
  const currentContextIDs = useMemo(
    () =>
      user?.establishment_config_ids?.length
        ? user.establishment_config_ids
        : user?.establishment_config_id
          ? [user.establishment_config_id]
          : [],
    [user?.establishment_config_id, user?.establishment_config_ids],
  );
  const normalizedSearch = search.trim().toLowerCase();
  const visibleOptions = useMemo(
    () =>
      normalizedSearch
        ? options.filter((item) =>
            item.name.toLowerCase().includes(normalizedSearch),
          )
        : options,
    [normalizedSearch, options],
  );
  const visibleOptionIDs = useMemo(
    () => visibleOptions.map((item) => item.establishment_config_id),
    [visibleOptions],
  );
  const allSelected =
    allOptionIDs.length > 0 && selected.length === allOptionIDs.length;
  const allVisibleSelected =
    visibleOptionIDs.length > 0 &&
    visibleOptionIDs.every((id) => selected.includes(id));
  const partiallySelected =
    visibleOptionIDs.some((id) => selected.includes(id)) && !allVisibleSelected;

  useEffect(() => {
    if (shouldRequestContext && options.length > 0) {
      const availableIDs = new Set(allOptionIDs);
      const selectedIDs = currentContextIDs.filter((id) =>
        availableIDs.has(id),
      );

      setSearch('');
      if (allowsMultipleContexts) {
        setSelected(selectedIDs.length > 0 ? selectedIDs : allOptionIDs);
        return;
      }

      setSelected(
        selectedIDs.length > 0 ? [selectedIDs[0]] : allOptionIDs.slice(0, 1),
      );
    }
  }, [
    allOptionIDs,
    allowsMultipleContexts,
    currentContextIDs,
    options.length,
    shouldRequestContext,
  ]);

  if (!isAuthenticated || !shouldRequestContext || !moduleRequiresContext) {
    return null;
  }

  const handleConfirm = async () => {
    if (selected.length === 0) return;

    try {
      await selectEstablishmentContext(
        allowsMultipleContexts ? selected : selected[0],
      );
      if (contextSelectionRedirectRoute) {
        navigate(contextSelectionRedirectRoute);
      }
      toast({
        title: t('establishment_context_modal.success_description'),
        variant: 'success',
      });
    } catch {
      toast({
        title: t('establishment_context_modal.error_description'),
        variant: 'destructive',
      });
    }
  };

  const handleCancelContextSelection = () => {
    if (isSelectingContext) return;

    useAuthStore.setState({
      contextSelectionRequired: false,
      contextSelectionRedirectRoute: null,
    });
  };

  const handleToggleAll = (checked: boolean) => {
    if (!allowsMultipleContexts) return;

    if (!normalizedSearch) {
      setSelected(checked ? allOptionIDs : []);
      return;
    }

    setSelected((current) =>
      checked
        ? Array.from(new Set([...current, ...visibleOptionIDs]))
        : current.filter((id) => !visibleOptionIDs.includes(id)),
    );
  };

  const handleToggleEstablishment = (id: string, checked: boolean) => {
    if (!allowsMultipleContexts) {
      setSelected(checked ? [id] : []);
      return;
    }

    setSelected((current) =>
      checked
        ? Array.from(new Set([...current, id]))
        : current.filter((item) => item !== id),
    );
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-6 backdrop-blur-[1px]"
      onClick={handleCancelContextSelection}
    >
      <div
        className="w-full max-w-lg rounded-xl border bg-background p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-ml-2 -mt-2"
            onClick={handleCancelContextSelection}
            disabled={isSelectingContext}
            aria-label="Cancelar seleção de contexto"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-foreground">
              {t('establishment_context_modal.title')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('establishment_context_modal.description')}
            </p>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg border bg-card">
          <div className="border-b p-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Pesquisar estabelecimento"
              disabled={isSelectingContext}
            />
          </div>

          {allowsMultipleContexts && (
            <label className="flex cursor-pointer items-center gap-3 border-b px-4 py-3 text-sm font-medium text-card-foreground transition-colors hover:bg-accent/60">
              <Checkbox
                checked={
                  (normalizedSearch ? allVisibleSelected : allSelected) ||
                  (partiallySelected ? 'indeterminate' : false)
                }
                onCheckedChange={(checked) => handleToggleAll(Boolean(checked))}
                disabled={isSelectingContext || visibleOptions.length === 0}
              />
              <span>
                {normalizedSearch ? 'Selecionar resultados' : 'Selecionar todos'}
              </span>
            </label>
          )}

          <div className="max-h-72 overflow-y-auto p-2">
            {visibleOptions.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Nenhum estabelecimento encontrado
              </div>
            )}

            {visibleOptions.map((item) => {
              const id = item.establishment_config_id;
              const checked = selected.includes(id);

              return (
                <label
                  key={id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-card-foreground transition-colors hover:bg-accent/60"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) =>
                      handleToggleEstablishment(id, Boolean(value))
                    }
                    disabled={isSelectingContext}
                  />
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleConfirm}
            disabled={selected.length === 0 || isSelectingContext}
          >
            {isSelectingContext
              ? t('establishment_context_modal.loading_button')
              : t('establishment_context_modal.confirm_button')}
          </Button>
        </div>
      </div>
    </div>
  );
};
